import type { ReactNode } from 'react'

import { cn } from '../utilitarios/cn'
import { Esqueleto } from './esqueleto'

export type TomIndicador = 'neutro' | 'marca' | 'sucesso' | 'atencao' | 'perigo' | 'info'

const TONS: Record<TomIndicador, string> = {
  neutro: 'text-conteudo',
  marca: 'text-marca-600',
  sucesso: 'text-sucesso',
  atencao: 'text-atencao',
  perigo: 'text-perigo',
  info: 'text-info',
}

export interface PropriedadesCartaoIndicador {
  rotulo: string
  valor: ReactNode
  descricao?: ReactNode
  icone?: ReactNode
  tom?: TomIndicador
  carregando?: boolean
  onSelecionar?: () => void
  className?: string
}

/**
 * Cartão de indicador (stat tile) — "Leads Finalizados: 216" e "Agendamentos: 4"
 * da TELA-01.
 *
 * Um número solto **não** vira gráfico: quando o dado é um valor único, o cartão é
 * a forma correta, e a rosca ao lado existe só porque ali há composição.
 *
 * `onSelecionar` faz o cartão inteiro virar o drill-down do bloco — o mesmo
 * conjunto de filtros que o servidor devolveu.
 */
export function CartaoIndicador({
  rotulo,
  valor,
  descricao,
  icone,
  tom = 'neutro',
  carregando,
  onSelecionar,
  className,
}: PropriedadesCartaoIndicador) {
  const Elemento = onSelecionar ? 'button' : 'div'

  return (
    <Elemento
      {...(onSelecionar ? { type: 'button' as const, onClick: onSelecionar } : {})}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border border-borda bg-superficie p-4 text-left shadow-sm',
        onSelecionar &&
          'transition-colors hover:border-marca-300 hover:bg-marca-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marca-500/40',
        className,
      )}
    >
      {icone ? (
        <span
          className={cn(
            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-superficie-2',
            TONS[tom],
          )}
        >
          {icone}
        </span>
      ) : null}

      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wide text-conteudo-fraco">{rotulo}</p>
        {carregando ? (
          <Esqueleto className="mt-1.5 h-7 w-20" />
        ) : (
          <p className={cn('mt-0.5 text-2xl font-semibold leading-tight', TONS[tom])}>{valor}</p>
        )}
        {descricao ? (
          <p className="mt-1 text-xs leading-snug text-conteudo-suave">{descricao}</p>
        ) : null}
      </div>
    </Elemento>
  )
}
